import React from 'react';
import PropTypes from 'prop-types';
import { Helmet } from 'react-helmet-async';

const commonMetaData = (title) => ({
  frontendUrl: 'https://buildexstores.com',
  title: `${title || 'Home'}`,
  favIcon: '/assets/images/logs/favIcon.png',
  twitterSite: '@Buildex',
  description:
    'Welcome to Buildex! Shop a wide range of quality products from top brands. Find the best deals on electronics, fashion, home appliances, and more. Fast shipping and great prices await you!',
  image: {
    url: '/assets/images/logs/favIcon.png',
    width: 1200,
    height: 630,
    alt: 'Buildex E-Commerce Platform',
  },
  type: 'website',
  url: 'https://buildexstores.com',
  card: 'summary_large_image',
});

const MetaHead = ({
  title,
  description,
  keywords,
  openGraph,
  twitter,
  canonical,
  structuredData,
}) => {
  const defaultMeta = commonMetaData(title);
  const openGraphImage = openGraph?.image ? openGraph?.image : defaultMeta.image;
  const structuredDataJson = structuredData
    ? JSON.stringify(structuredData).replace(/</g, '\\u003c')
    : null;

  return (
    <Helmet>
      {/* Primary Meta Tags */}
      <title>{`${title || 'Home'}` || defaultMeta.title}</title>
      <meta name="description" content={description || defaultMeta.description} />
      <meta name="keywords" content={keywords || ''} />
      <meta name="robots" content="index, follow" />
      <link rel="canonical" href={canonical} />

      {/* Structured Data */}
      {structuredDataJson && (
        <script type="application/ld+json">{structuredDataJson}</script>
      )}

      {/* Open Graph Meta Tags */}
      {openGraph && (
        <>
          <meta property="og:title" content={openGraph.title || defaultMeta.title} />
          <meta
            property="og:description"
            content={openGraph.description || defaultMeta.description}
          />
          <meta property="og:url" content={defaultMeta.url} />
          <meta property="og:type" content={defaultMeta.type || 'website'} />

          <meta property="og:image" content={openGraphImage?.url} />
        </>
      )}

      {/* Twitter Meta Tags */}
      {twitter && (
        <>
          <meta name="twitter:card" content={defaultMeta.card} />
          <meta name="twitter:site" content={defaultMeta.twitterSite} />
          <meta name="twitter:title" content={twitter.title || defaultMeta.title} />
          <meta
            name="twitter:description"
            content={twitter.description || defaultMeta.description}
          />
          <meta name="twitter:image" content={twitter?.image?.url || defaultMeta.favIcon} />
        </>
      )}

      {/* Favicon */}
      <link rel="icon" href={defaultMeta.favIcon} type="image/png" />
    </Helmet>
  );
};

MetaHead.propTypes = {
  title: PropTypes.string,
  description: PropTypes.string,
  keywords: PropTypes.string,
  canonical: PropTypes.string,
  structuredData: PropTypes.object,

  openGraph: PropTypes.shape({
    title: PropTypes.string,
    description: PropTypes.string,
    url: PropTypes.string,
    type: PropTypes.string,
    image: PropTypes.shape({
      url: PropTypes.string.isRequired,
      alt: PropTypes.string,
    }),
  }),
  twitter: PropTypes.shape({
    card: PropTypes.string,
    site: PropTypes.string,
    title: PropTypes.string,
    description: PropTypes.string,
    image: PropTypes.shape({
      url: PropTypes.string.isRequired,
      alt: PropTypes.string,
    }),
  }),
};

export default MetaHead;
